/**
 * Pure workflow graph mutations — workflow_steps is the source of truth for navigation.
 */

export function appendNavigationAction(workflow, sourceId, targetId, text = '') {
  if (!workflow) return workflow;

  return {
    ...workflow,
    workflow_steps: workflow.workflow_steps.map((step) => {
      if (step.id !== sourceId) return step;
      return {
        ...step,
        actions: [
          ...(step.actions || []),
          { type: 'navigation', link: targetId, text },
        ],
      };
    }),
  };
}

/**
 * Remove steps by id and strip navigation actions that point at any removed id.
 */
export function removeStepsById(workflow, deletedIds) {
  if (!workflow || !Array.isArray(workflow.workflow_steps)) return workflow;

  const idSet = new Set((deletedIds || []).filter(Boolean));
  if (idSet.size === 0) return workflow;

  const workflow_steps = workflow.workflow_steps
    .filter((step) => !idSet.has(step.id))
    .map((step) => {
      if (!step.actions?.length) return step;
      const actions = step.actions.filter(
        (action) => action.type !== 'navigation' || !idSet.has(action.link)
      );
      return actions.length === step.actions.length ? step : { ...step, actions };
    });

  return { ...workflow, workflow_steps };
}

/**
 * Build sorted deletion descriptors from React Flow edge delete events.
 */
export function buildEdgeDeletionList(deletedEdges, parseEdgeId) {
  return (deletedEdges || [])
    .map((edge) => ({
      source: edge.source,
      actionIndex: edge.data?.actionIndex ?? parseEdgeId(edge.id)?.actionIndex,
    }))
    .filter((d) => d.actionIndex != null)
    .sort((a, b) =>
      a.source === b.source ? b.actionIndex - a.actionIndex : a.source.localeCompare(b.source)
    );
}

/**
 * Remove navigation actions by source step and action index (highest index first per source).
 */
export function removeNavigationActions(workflow, deletions) {
  if (!workflow || !deletions?.length) return workflow;

  const workflow_steps = workflow.workflow_steps.map((step) => ({
    ...step,
    actions: [...(step.actions || [])],
  }));

  deletions.forEach(({ source, actionIndex }) => {
    const sourceStep = workflow_steps.find((step) => step.id === source);
    if (sourceStep?.actions?.[actionIndex] != null) {
      sourceStep.actions.splice(actionIndex, 1);
    }
  });

  return { ...workflow, workflow_steps };
}
